import { forwardRef } from 'react';
import { ArrowRight, CalendarDays, MapPin } from 'lucide-react';
import TemplateFrame from './TemplateFrame';
import ClubBrandMark from './ClubBrandMark';
import GuestStyleGridTable from './GuestStyleGridTable';
import MultiDayCompactTable from './MultiDayCompactTable';
import PickleballBall from './PickleballBall';
import PickleballPaddle from './PickleballPaddle';
import { AVAILABILITY_IMAGE_PORTRAIT, AVAILABILITY_IMAGE_STORY } from '../../constants/bookingConst';
import { fitClubNameSize } from '../../utils/availabilitySnapshotUtil';
import '../../css/AvailabilityGrid.css';
import '../../css/AvailabilityMobile.css';

// Per-format sizing (see planGridRows). bodyHeight is what is left for the
// hour rows once the header, title, table header and footer are
// placed. On a phone the image shows at about a third of its width, so rows
// stay hourly only while they come out at least ~1.6× the guest page's size
// in the image; below that hours are merged into ranges.
// `compact` sizes the multi-day table (a date range; see
// planCompactGridRows): its rows share the panel with the date header (and a
// short legend when there are promos). Its "Time ranges" images merge hours
// whenever hourly rows would be under minHourlyRowHeight — a week of courts
// stacked in one row needs the height for its words — while "Per hour"
// keeps every hour.
const LAYOUTS = {
  story: {
    size: AVAILABILITY_IMAGE_STORY,
    grid: { bodyHeight: 910, minHourlyUnit: 1.6, maxUnit: 2.6, maxRowHeight: 130, tableWidth: 920, maxHeaderUnit: 2.2 },
    compact: { bodyHeight: 1100, tableWidth: 960, minHourlyRowHeight: 84, maxRowHeight: 130 },
  },
  portrait: {
    size: AVAILABILITY_IMAGE_PORTRAIT,
    grid: { bodyHeight: 664, minHourlyUnit: 1.5, maxUnit: 2.2, maxRowHeight: 110, tableWidth: 928, maxHeaderUnit: 1.9 },
    compact: { bodyHeight: 690, tableWidth: 960, minHourlyRowHeight: 64, maxRowHeight: 100 },
  },
};

/**
 * The phone-sized "Court Availability" image, shared by the dark and light
 * designs: brand, date, a title, the timeslot grid, and an
 * open-slots / Book Now footer. Given a multi-day snapshot (a date range in
 * the dialog) it draws the compact days-across table instead of the
 * single-day grid, and the range's dates and title. Only colours and decoration differ between
 * the two, and those live in AvailabilityMobile.css under `.agm-dark` /
 * `.agm-light`.
 *
 * @param {object} props
 * @param {object} props.snapshot - buildAvailabilitySnapshot() or buildMultiDaySnapshot() output
 * @param {'story'|'portrait'} props.format - 1080×1920 or 1080×1350
 * @param {'dark'|'light'} props.variant
 * @param {'ranges'|'hourly'} [props.rowMode] - 'hourly' forces one row per hour; 'ranges' merges hours when hourly rows would be too small
 * @param {object} [props.imageSettings] - Availability images settings (font sizes in px, BOOKED colours); see availabilityImageSettingsUtil
 * @returns {JSX.Element}
 */
const FullGridMobileLayout = forwardRef(function FullGridMobileLayout({ snapshot, format = 'story', variant, rowMode, imageSettings }, ref) {
  const layout = LAYOUTS[format] || LAYOUTS.story;
  // Drawn icons take their colour as a prop: the club's Active Button Color,
  // made to read on this design's background.
  const iconColor = variant === 'dark' ? snapshot.palette.accentOnDark : snapshot.palette.accentOnLight;
  const story = format === 'story';
  const note = snapshot.hasPromo ? 'Promo rates on select hours' : snapshot.hasRateRange ? 'Peak hours apply' : '';
  const multiDay = Boolean(snapshot.isMultiDay);
  // "Weekly court availability" -> "Weekly court" + accented "availability".
  const titleWords = multiDay ? snapshot.title.split(' ') : ['Court', 'Availability'];
  const titleAccent = titleWords.pop();

  return (
    <TemplateFrame
      ref={ref}
      width={layout.size.width}
      height={layout.size.height}
      className={`agm agm-${variant} agm-${format}${multiDay ? ' agm-multi' : ''}`}
      palette={snapshot.palette}
    >
      {/* ---- decoration ---- */}
      <div className="agm-deco-corner" />
      <div className="agm-deco-stripe agm-deco-stripe-1" />
      <div className="agm-deco-stripe agm-deco-stripe-2" />
      <div className="agm-deco-dots" />
      <PickleballPaddle width={story ? 230 : 170} className="agm-paddle" />
      <PickleballBall size={story ? 120 : 86} className="agm-ball" />

      {/* ---- header ---- */}
      <div className="agm-header">
        <ClubBrandMark
          snapshot={snapshot}
          iconSize={story ? 70 : 58}
          iconColor={iconColor}
          nameSize={fitClubNameSize(snapshot.clubNameMain, story ? 42 : 34, 20)}
          className="agm-mark"
        />
      </div>
      <div className="agm-date">
        <CalendarDays size={story ? 38 : 32} color={iconColor} strokeWidth={2} />
        <span>{snapshot.weekdayUpper}</span>
        <span className="agm-date-full">{snapshot.dateCompactUpper}</span>
      </div>
      <div className="agm-title">
        <span>{titleWords.join(' ')}</span> <em>{titleAccent}</em>
      </div>
      <div className="agm-subtitle">Reserve your court. Play your game.</div>

      {/* ---- table ---- */}
      <div className="agm-stage">
        <div className="agm-panel">
          {multiDay ? (
            <MultiDayCompactTable snapshot={snapshot} sizing={layout.compact} rowMode={rowMode} imageSettings={imageSettings} />
          ) : (
            <GuestStyleGridTable
              snapshot={snapshot}
              bodyHeight={layout.grid.bodyHeight}
              minHourlyUnit={layout.grid.minHourlyUnit}
              maxUnit={layout.grid.maxUnit}
              maxRowHeight={layout.grid.maxRowHeight}
              tableWidth={layout.grid.tableWidth}
              maxHeaderUnit={layout.grid.maxHeaderUnit}
              rowMode={rowMode}
              imageSettings={imageSettings}
            />
          )}
        </div>
      </div>

      {/* ---- footer ---- */}
      <div className="agm-footer">
        <div className="agm-slots">
          <span className="agm-slots-label">Open slots</span>
          <span className="agm-slots-value">{snapshot.openCount}<small> of </small>{snapshot.totalCount}</span>
          {note && <span className="agm-slots-note">{note}</span>}
        </div>
        <div className="agm-cta">
          <span className="agm-cta-btn">Book now <ArrowRight size={story ? 36 : 30} strokeWidth={2.6} /></span>
          {snapshot.siteHost && (
            <span className="agm-cta-host"><MapPin size={story ? 28 : 24} color={iconColor} strokeWidth={2} />{snapshot.siteHost}</span>
          )}
        </div>
      </div>
    </TemplateFrame>
  );
});

export default FullGridMobileLayout;
